/**
 * AppointmentList component.
 *
 * Renders selectable appointment cards grouped into upcoming and past sections.
 * Upcoming appointments are clickable buttons that trigger the reschedule/cancel
 * flow. Past appointments (within 1 week) are displayed as read-only cards with
 * muted styling. A caveat warns that direct clinic bookings may not appear.
 */

import type {
  ChatMessage,
  AppointmentData,
  AppointmentListCallbacks,
} from "../../types";

export interface AppointmentListOptions {
  message: ChatMessage;
  callbacks: AppointmentListCallbacks;
}

export class AppointmentList {
  private readonly element: HTMLElement;
  private selectedId: string | null = null;

  constructor(options: AppointmentListOptions) {
    const { message, callbacks } = options;
    const appointments = message.appointments ?? [];

    this.element = document.createElement("div");
    this.element.className = "hana-appointment";
    this.element.setAttribute("data-message-id", message.id);
    this.element.setAttribute("role", "list");
    this.element.setAttribute("aria-label", "Appointment list");

    // Header text
    if (message.content) {
      const header = document.createElement("div");
      header.className = "hana-appointment__header";
      header.textContent = message.content;
      this.element.appendChild(header);
    }

    if (appointments.length === 0) {
      // Empty state
      const empty = document.createElement("div");
      empty.className = "hana-appointment__empty";
      empty.textContent =
        "No appointments found. You can book a new appointment or contact the clinic directly for assistance.";
      this.element.appendChild(empty);
    } else {
      // Separate into upcoming and past
      const upcoming = appointments.filter(
        (a) => (a.section ?? "upcoming") === "upcoming",
      );
      const past = appointments.filter((a) => a.section === "past");

      // Render upcoming first
      if (upcoming.length > 0) {
        this.element.appendChild(
          this.createSection(
            "upcoming",
            "Upcoming Appointments",
            upcoming,
            callbacks,
          ),
        );
      }

      // Then past
      if (past.length > 0) {
        this.element.appendChild(
          this.createSection("past", "Past Appointments", past, callbacks),
        );
      }
    }

    // Caveat text
    if (message.caveat) {
      const caveat = document.createElement("div");
      caveat.className = "hana-appointment__caveat";
      caveat.textContent = message.caveat;
      this.element.appendChild(caveat);
    }
  }

  getElement(): HTMLElement {
    return this.element;
  }

  getSelectedId(): string | null {
    return this.selectedId;
  }

  private createSection(
    type: "upcoming" | "past",
    heading: string,
    appointments: AppointmentData[],
    callbacks: AppointmentListCallbacks,
  ): HTMLElement {
    const section = document.createElement("div");
    section.className = `hana-appointment__section hana-appointment__section--${type}`;

    const headingEl = document.createElement("div");
    headingEl.className = "hana-appointment__section-heading";
    headingEl.textContent = heading;
    section.appendChild(headingEl);

    appointments.forEach((apt) => {
      const card = this.createCard(apt, type, callbacks);
      section.appendChild(card);
    });

    return section;
  }

  private createCard(
    apt: AppointmentData,
    sectionType: "upcoming" | "past",
    callbacks: AppointmentListCallbacks,
  ): HTMLElement {
    const isPast = sectionType === "past";

    // Upcoming cards are buttons (clickable); past cards are divs (read-only)
    const card = document.createElement(isPast ? "div" : "button");
    card.className = "hana-appointment__card";
    card.setAttribute("data-appointment-id", apt.id);
    card.setAttribute("role", "listitem");

    if (isPast) {
      card.classList.add("hana-appointment__card--past");
    }

    if (!isPast) {
      (card as HTMLButtonElement).type = "button";
    }

    // Date and time row
    const dateTimeEl = document.createElement("div");
    dateTimeEl.className = "hana-appointment__datetime";
    dateTimeEl.textContent = `${apt.date} at ${apt.time}`;
    card.appendChild(dateTimeEl);

    // Doctor name
    const doctorEl = document.createElement("div");
    doctorEl.className = "hana-appointment__doctor";
    doctorEl.textContent = apt.doctorName;
    card.appendChild(doctorEl);

    // Specialty
    const specialtyEl = document.createElement("div");
    specialtyEl.className = "hana-appointment__specialty";
    specialtyEl.textContent = apt.specialty;
    card.appendChild(specialtyEl);

    // Location
    const locationEl = document.createElement("div");
    locationEl.className = "hana-appointment__location";
    locationEl.textContent = apt.location;
    card.appendChild(locationEl);

    // Status badge
    const statusEl = document.createElement("span");
    statusEl.className = "hana-appointment__status";
    statusEl.textContent = apt.status;
    card.appendChild(statusEl);

    // Reschedule/cancel counters (upcoming only)
    if (!isPast) {
      if (apt.reschedulesRemaining !== undefined) {
        const counter = document.createElement("div");
        counter.className =
          "hana-appointment__counter hana-appointment__counter--reschedule";
        counter.textContent = `${apt.reschedulesRemaining} reschedule${apt.reschedulesRemaining !== 1 ? "s" : ""} remaining`;
        card.appendChild(counter);
      }

      if (apt.cancelsRemaining !== undefined) {
        const counter = document.createElement("div");
        counter.className =
          "hana-appointment__counter hana-appointment__counter--cancel";
        counter.textContent = `${apt.cancelsRemaining} cancel${apt.cancelsRemaining !== 1 ? "s" : ""} remaining`;
        card.appendChild(counter);
      }
    }

    // Click handler for upcoming appointments only
    if (!isPast) {
      card.addEventListener("click", () => {
        if (this.selectedId !== null) return; // Single select

        this.selectedId = apt.id;

        // Disable all upcoming cards
        const allCards = this.element.querySelectorAll(
          ".hana-appointment__card:not(.hana-appointment__card--past)",
        );
        allCards.forEach((el) => {
          (el as HTMLButtonElement).disabled = true;
        });

        card.classList.add("hana-appointment__card--selected");
        callbacks.onSelect(apt.id);
      });
    }

    return card;
  }
}
